"use client"

import { useRef, useState } from "react"
import { trackEvent } from "@/lib/analytics"

type Ask = { kicker: string; prompt: string; does: string }

const SPORTS: { id: string; label: string; asks: Ask[] }[] = [
  {
    id: "running",
    label: "Running",
    asks: [
      {
        kicker: "HOW AM I DOING",
        prompt: "How's my training going?",
        does: "Your plan as a card: done, missed and next, with the next session's steps.",
      },
      {
        kicker: "TODAY",
        prompt: "Should I go hard today or hold back?",
        does: "Readiness, overnight HRV, sleep and recent load, in one answer.",
      },
      {
        kicker: "ONE SESSION",
        prompt: "Build me a 45 minute tempo run and put it on my watch for Thursday.",
        does: "Target paces from your own running, synced to your watch.",
      },
      {
        kicker: "A WHOLE BLOCK",
        prompt: "I'm running a half marathon on 15 March. Build me a plan.",
        does: "Designed with you, then every session scheduled in one go.",
      },
      {
        kicker: "AFTER A RUN",
        prompt: "Why did my last run feel so hard?",
        does: "Splits, heart rate, weather, hills and earlier runs of the same route.",
      },
      {
        kicker: "PROGRESS",
        prompt: "What could I run for a half today, and am I getting fitter?",
        does: "Race predictions, VO2 max and threshold, month by month.",
      },
    ],
  },
  {
    id: "cycling",
    label: "Cycling",
    asks: [
      {
        kicker: "HOW AM I DOING",
        prompt: "How's my training going?",
        does: "Your plan as a card, or your recent weeks of riding if you don't have one.",
      },
      {
        kicker: "TODAY",
        prompt: "Should I go hard today or hold back?",
        does: "Readiness, overnight HRV, sleep and recent load, in one answer.",
      },
      {
        kicker: "ONE SESSION",
        prompt: "Build me a sweet spot ride, 3 x 12 minutes, for Saturday.",
        does: "Watts from your FTP with a power meter or smart trainer, heart rate without.",
      },
      {
        kicker: "A WHOLE BLOCK",
        prompt: "Build me an 8 week block to lift my FTP, three rides a week.",
        does: "Designed with you, then every ride scheduled in one go.",
      },
      {
        kicker: "AFTER A RIDE",
        prompt: "How did my ride go?",
        does: "Normalised power, training stress, best efforts, climbs and weather.",
      },
      {
        kicker: "PROGRESS",
        prompt: "Has my FTP gone up this year?",
        does: "FTP and watts per kilo, month by month.",
      },
    ],
  },
]

/** One prompt as a card that copies it. */
function AskCard({ ask, sport }: { ask: Ask; sport: string }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  const copy = () => {
    navigator.clipboard?.writeText(ask.prompt).catch(() => {
      // Blocked clipboard: the text is on screen to select by hand.
    })
    trackEvent("copied_guide_prompt", { section: sport, kicker: ask.kicker, prompt: ask.prompt })
    setCopied(true)
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setCopied(false), 1600)
  }

  return (
    <button
      type="button"
      onClick={copy}
      className="group flex h-full flex-col gap-2 rounded-[14px] border border-[#E6E8EC] bg-white p-4 text-left hover:border-[#C9CDD4] [@media(hover:none)]:active:opacity-60"
    >
      <span className="flex items-center justify-between gap-3">
        <span className="font-mono text-[11px] tracking-[0.1em] text-[#C8261F]">{ask.kicker}</span>
        <span className="text-xs font-medium text-[#5B6270] group-hover:text-[#C8261F]">{copied ? "Copied" : "Copy"}</span>
      </span>
      <span className="text-[16px] font-semibold leading-snug tracking-[-0.01em] text-[#161B26]">&ldquo;{ask.prompt}&rdquo;</span>
      <span className="text-sm leading-normal text-[#5B6270]">{ask.does}</span>
    </button>
  )
}

/** What to ask, one sport at a time. */
export function GuidePrompts() {
  const [active, setActive] = useState(SPORTS[0].id)
  const sport = SPORTS.find((s) => s.id === active) ?? SPORTS[0]

  return (
    <div className="not-prose my-6 flex flex-col gap-4">
      <div role="tablist" aria-label="Sport" className="inline-flex self-start rounded-full border border-[#E6E8EC] bg-white p-1">
        {SPORTS.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            id={`tab-${s.id}`}
            aria-selected={s.id === active}
            aria-controls={`panel-${s.id}`}
            onClick={() => setActive(s.id)}
            className={`h-9 rounded-full px-4 text-[15px] font-medium ${
              s.id === active ? "bg-[#161B26] text-white" : "text-[#5B6270] hover:text-[#161B26]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`panel-${sport.id}`}
        aria-labelledby={`tab-${sport.id}`}
        className="grid grid-cols-1 gap-3 min-[640px]:grid-cols-2"
      >
        {sport.asks.map((a) => (
          <AskCard key={`${sport.id}-${a.kicker}`} ask={a} sport={sport.id} />
        ))}
      </div>
      <span className="text-[13.5px] text-[#5B6270]">Tap a card to copy it, then paste it into Claude.</span>
    </div>
  )
}
